import { useState, useMemo, useCallback } from 'react';
import type { DeliveryAttentionItem, OutcomeSummary } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface OutcomeTableProps {
  outcomes: OutcomeSummary[];
  attentionItems?: DeliveryAttentionItem[];
  onExecute: (outcomeId: string) => void;
  onView: (outcomeId: string) => void;
}

type SortField = 'id' | 'title' | 'status' | 'convergenceScore';
type SortDir = 'asc' | 'desc';

const ACTIONABLE_STATUSES = new Set(['approved', 'in-progress', 'rework', 'converging']);

const STATUS_ORDER: Record<string, number> = {
  'in-progress': 0,
  converging: 1,
  rework: 2,
  approved: 3,
  complete: 4,
};

function getConvergenceColor(score: number | null): string {
  if (score === null) return 'text-text-tertiary';
  if (score >= 90) return 'text-success';
  if (score >= 70) return 'text-warning';
  return 'text-error';
}

function getStatusBadgeVariant(status: string): 'default' | 'secondary' {
  return status === 'complete' ? 'default' : 'secondary';
}

function compareOutcomes(
  a: OutcomeSummary,
  b: OutcomeSummary,
  field: SortField,
  dir: SortDir
): number {
  let cmp = 0;

  switch (field) {
    case 'id':
      cmp = a.id.localeCompare(b.id);
      break;
    case 'title':
      cmp = a.title.localeCompare(b.title);
      break;
    case 'status': {
      const aOrder = STATUS_ORDER[a.status] ?? 99;
      const bOrder = STATUS_ORDER[b.status] ?? 99;
      cmp = aOrder - bOrder;
      break;
    }
    case 'convergenceScore': {
      const aScore = a.convergenceScore ?? -1;
      const bScore = b.convergenceScore ?? -1;
      cmp = aScore - bScore;
      break;
    }
  }

  return dir === 'asc' ? cmp : -cmp;
}

function OutcomeTable({
  outcomes,
  attentionItems = [],
  onExecute,
  onView,
}: OutcomeTableProps): React.ReactElement {
  const [sortField, setSortField] = useState<SortField>('status');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const attentionByOutcomeId = useMemo(
    () => new Map(attentionItems.map(item => [item.outcome.id, item])),
    [attentionItems]
  );

  const handleSort = useCallback(
    (field: SortField): void => {
      if (field === sortField) {
        setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortField(field);
        setSortDir('asc');
      }
    },
    [sortField]
  );

  const sorted = useMemo(
    (): OutcomeSummary[] => [...outcomes].sort((a, b) => compareOutcomes(a, b, sortField, sortDir)),
    [outcomes, sortField, sortDir]
  );

  function renderSortIndicator(field: SortField): string {
    if (field !== sortField) return '';
    return sortDir === 'asc' ? ' \u2191' : ' \u2193';
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Outcomes</CardTitle>
      </CardHeader>
      <CardContent>
        <table className="w-full text-sm">
          <thead>
            <tr>
              {(
                [
                  ['id', 'ID'],
                  ['title', 'Title'],
                  ['status', 'Status'],
                  ['convergenceScore', 'Convergence'],
                ] as const
              ).map(([field, label]) => (
                <th
                  key={field}
                  onClick={() => {
                    handleSort(field);
                  }}
                  className="cursor-pointer select-none border-b border-border px-3 py-2 text-left text-text-secondary font-medium hover:text-text-primary"
                >
                  {label}
                  {renderSortIndicator(field)}
                </th>
              ))}
              <th className="border-b border-border px-3 py-2 text-left text-text-secondary font-medium">
                Deps
              </th>
              <th className="border-b border-border px-3 py-2 text-left text-text-secondary font-medium">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(outcome => {
              const isActionable = ACTIONABLE_STATUSES.has(outcome.status);
              const attention = attentionByOutcomeId.get(outcome.id);
              const blockedBy = attention?.blockedBy ?? [];
              const isBlocked = attention?.hardBlocked ?? false;
              const attentionSummary =
                attention && !attention.hardBlocked ? attention.reason.replace('_', ' ') : null;

              return (
                <tr key={outcome.id} className="hover:bg-surface-elevated/50">
                  <td className="border-b border-border px-3 py-2 font-mono text-xs text-text-secondary">
                    {outcome.id}
                  </td>
                  <td className="border-b border-border px-3 py-2 text-text-primary">
                    {outcome.title}
                  </td>
                  <td className="border-b border-border px-3 py-2">
                    <Badge variant={getStatusBadgeVariant(outcome.status)}>{outcome.status}</Badge>
                  </td>
                  <td
                    className={cn(
                      'border-b border-border px-3 py-2 font-medium',
                      getConvergenceColor(outcome.convergenceScore)
                    )}
                  >
                    {outcome.convergenceScore !== null ? `${outcome.convergenceScore}%` : '--'}
                  </td>
                  <td className="border-b border-border px-3 py-2">
                    {outcome.dependsOn && outcome.dependsOn.length > 0 ? (
                      <div className="space-y-1">
                        <span
                          className="rounded-full bg-surface-elevated px-2 py-0.5 text-[10px] font-medium text-text-secondary"
                          title={outcome.dependsOn.join(', ')}
                        >
                          {outcome.dependsOn.length} dep{outcome.dependsOn.length > 1 ? 's' : ''}
                        </span>
                        {isBlocked && (
                          <div className="text-[10px] font-medium text-warning">
                            blocked by {blockedBy.join(', ')}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-text-tertiary text-xs">{attentionSummary ?? '—'}</span>
                    )}
                  </td>
                  <td className="border-b border-border px-3 py-2">
                    {isActionable ? (
                      <Button
                        size="xs"
                        disabled={isBlocked}
                        title={
                          isBlocked ? `Blocked by: ${blockedBy.join(', ')}` : attention?.message
                        }
                        onClick={() => {
                          onExecute(outcome.id);
                        }}
                      >
                        {isBlocked ? 'Blocked' : 'Execute'}
                      </Button>
                    ) : (
                      <Button
                        size="xs"
                        variant="ghost"
                        onClick={() => {
                          onView(outcome.id);
                        }}
                      >
                        View
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

export { OutcomeTable };
export type { OutcomeTableProps };
